import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getOverrideRate } from '../../api/override-rate'
import type { OverrideRateGroupBy } from '@lms/shared-types'
import { ApiError } from '../../api/client'

export interface OverrideRateTileProps {
  from?: string
  to?: string
}

const GROUP_BY_OPTIONS: { value: OverrideRateGroupBy; label: string; testId: string }[] = [
  { value: 'reason', label: 'Reason', testId: 'group-by-reason' },
  { value: 'manager', label: 'Manager', testId: 'group-by-manager' },
  { value: 'month', label: 'Month', testId: 'group-by-month' },
]

function barColor(ratePct: number): 'ochre' | 'clay' {
  return ratePct >= 25 ? 'clay' : 'ochre'
}

/**
 * Override rate analytics tile — Sprint 6 F6.
 *
 * Displays the percentage of manager decisions that diverged from the AI
 * recommendation, grouped by reason, manager, or month.
 *
 * Gated externally on flags.aiOverrideReason in HrAuditPage.
 */
export function OverrideRateTile({ from, to }: OverrideRateTileProps): JSX.Element {
  const [groupBy, setGroupBy] = useState<OverrideRateGroupBy>('reason')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['override-rate', { from, to, group_by: groupBy }],
    queryFn: () =>
      getOverrideRate({
        ...(from != null ? { from } : {}),
        ...(to != null ? { to } : {}),
        group_by: groupBy,
      }),
    retry: false,
  })

  // ── 403 check ───────────────────────────────────────────────────────────────

  const is403 =
    error instanceof ApiError
      ? error.status === 403
      : error != null && (error as { status?: number }).status === 403

  if (is403) {
    return (
      <div
        data-testid="override-rate-403"
        style={{
          padding: '12px 16px',
          background: 'var(--danger-bg)',
          border: '1px solid var(--danger-line)',
          borderRadius: 'var(--r-md)',
          color: 'var(--danger-fg)',
          fontSize: 13,
        }}
      >
        You don&apos;t have permission to view override analytics.
      </div>
    )
  }

  // ── Loading skeleton ─────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div
        data-testid="override-rate-skeleton"
        style={{
          padding: '20px 24px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {[80, 60, 70].map((w, i) => (
          <div
            key={i}
            style={{
              height: 16,
              width: `${w}%`,
              borderRadius: 4,
              background: 'var(--border)',
              animation: 'shimmer 1.4s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    )
  }

  // ── Error state ──────────────────────────────────────────────────────────────

  if (error != null) {
    return (
      <div
        data-testid="override-rate-error"
        style={{
          padding: '12px 16px',
          background: 'var(--surface)',
          border: '1px solid var(--danger-line)',
          borderRadius: 'var(--r-md)',
          color: 'var(--danger-fg)',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span>Failed to load override analytics.</span>
        <button
          data-testid="override-rate-retry"
          type="button"
          onClick={() => void refetch()}
          style={{
            padding: '4px 10px',
            background: 'var(--danger)',
            color: 'var(--surface)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  // ── Success ──────────────────────────────────────────────────────────────────

  const totals = data?.totals
  const rows = data?.rows ?? []

  return (
    <div
      data-testid="override-rate-tile"
      style={{
        padding: '20px 24px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-3)',
          }}
        >
          Override rate
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
          Manager decisions that diverged from AI recommendation
        </span>
      </div>

      {/* Big number */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          data-testid="override-rate-pct"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 40,
            fontWeight: 700,
            color: 'var(--text)',
            lineHeight: 1,
          }}
        >
          {totals?.override_rate_pct.toFixed(1)}
        </span>
        <span style={{ fontSize: 20, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
          %
        </span>
      </div>

      {/* Summary line */}
      <div
        data-testid="override-rate-summary"
        style={{ fontSize: 13, color: 'var(--text-2)' }}
      >
        {totals?.overrides_total} of {totals?.decisions_total} decisions
      </div>

      {/* group_by toggle */}
      <div style={{ display: 'flex', gap: 6 }}>
        {GROUP_BY_OPTIONS.map(({ value, label, testId }) => {
          const pressed = groupBy === value
          return (
            <button
              key={value}
              type="button"
              data-testid={testId}
              aria-pressed={pressed}
              onClick={() => setGroupBy(value)}
              style={{
                minHeight: 44,
                padding: '0 14px',
                borderRadius: 999,
                border: pressed
                  ? '1.5px solid var(--steel-blue)'
                  : '1.5px solid var(--border)',
                background: pressed ? 'var(--primary-bg, rgba(195,82,58,0.08))' : 'transparent',
                color: pressed ? 'var(--steel-blue)' : 'var(--text-2)',
                fontSize: 12,
                fontWeight: pressed ? 700 : 400,
                cursor: 'pointer',
                transition: 'all 80ms ease',
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Bar list */}
      {rows.length > 0 ? (
        <div
          data-testid="override-rate-bars"
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {rows.map((row) => {
            const color = barColor(row.rate_pct)
            const barBg = color === 'clay' ? 'var(--danger)' : 'var(--warning)'
            return (
              <div
                key={row.label}
                data-testid="override-rate-bar-row"
                style={{ display: 'flex', flexDirection: 'column', gap: 3 }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: 'var(--text-2)',
                  }}
                >
                  <span>{row.label}</span>
                  <span
                    style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}
                  >
                    {row.rate_pct.toFixed(1)}% ({row.overrides}/{row.decisions})
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    background: 'var(--french-gray-2)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    data-testid="override-rate-bar"
                    data-color={color}
                    style={{
                      height: '100%',
                      width: `${Math.min(row.rate_pct, 100)}%`,
                      background: barBg,
                      borderRadius: 3,
                      transition: 'width 150ms ease',
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
